import { yupResolver } from '@hookform/resolvers/yup'
import Head from 'next/head'
import Image from 'next/image'
import Router, { useRouter } from 'next/router'
import { ReactElement, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from 'urql'
import { object, string } from 'yup'

import { LoginLayout } from '@/components/layout/web'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SignInMutation } from '@/queries'
import Link from '@/update-monitor/Link'
import { saveAuthentication } from '@/urql-common'

type LoginFormData = {
  username: string
  password: string
}

const schema = object({
  username: string().required(),
  password: string().required(),
})

export default function SignIn() {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
  })
  const router = useRouter()
  const [result, signInMutation] = useMutation(SignInMutation)
  const registrationType = router.query.registration_type as string
  const code = router.query.code as string

  useEffect(function redirectWhenSignedIn() {
    if (localStorage.getItem('accessToken')) Router.replace('/web/user-setting')
  }, [])

  const onSubmit = async ({ username, password }: LoginFormData) => {
    const { data } = await signInMutation({
      input: { email: username, password },
    })

    if (!data?.signIn?.authentication) return

    saveAuthentication(data.signIn.authentication)

    const userRole = data.signIn.userRole
    if (userRole === 'SCHOOL_ADMIN') {
      router.push('/web/school-admin')
    } else if (userRole === 'PARTNER_ADMIN') {
      router.push('/web/partner-admin')
    } else {
      router.push('/web/user-setting')
    }
  }

  return (
    <>
      <Head>
        <title>ロジックラボ | ログイン</title>
      </Head>

      <div className="grid h-full place-items-center px-5">
        <div className="w-full max-w-[720px] rounded-lg bg-white px-5 pb-[46px] pt-[80px] md:pb-10">
          <Image
            src="/gApp_logo.svg"
            width={257}
            height={68}
            className="mx-auto md:h-[80px] md:w-[300px]"
            alt="LogiQlabo"
          />

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="mx-auto mt-[34px] flex w-full max-w-[320px] flex-col items-center md:mt-[18px]"
          >
            <Input
              placeholder="メールアドレス"
              error={!!errors.username}
              className="w-full"
              {...register('username', { required: true })}
            />

            <Input
              type="password"
              placeholder="パスワード"
              error={!!errors.password}
              className="mt-[10px] w-full"
              {...register('password', { required: true })}
            />

            {result.data?.signIn?.errors &&
              result.data.signIn.errors.length > 0 && (
                <p className="mt-1 text-xs text-caution md:text-[13px]">
                  アカウント名 もしくはパスワードが正しくありません。
                </p>
              )}

            <div className="mt-[5px] w-full">
              <Link
                className="mt-2 inline-block text-xs font-medium text-primary-100"
                href="/web/password_forgot"
              >
                パスワードを忘れた方はこちら
              </Link>
            </div>

            <Button
              type="submit"
              variant="secondary"
              className="mx-auto mt-[34px] w-[180px]"
              disabled={!isValid}
            >
              ログイン
            </Button>
          </form>

          <Link
            href={{
              pathname: '/web/agreement',
              query: {
                registration_type: registrationType,
                code,
              },
            }}
            as="/web/agreement"
            className="mt-3 block text-center text-primary-100"
          >
            新規アカウント作成
          </Link>
        </div>
      </div>
    </>
  )
}

SignIn.getLayout = (page: ReactElement) => <LoginLayout>{page}</LoginLayout>
